/**
 * v4 shell — bilingual, professional. Bottom-tab nav for mobile, sticky
 * top bar with business identity + accountant + settings.
 */

import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { Home, FolderOpen, Briefcase, MessageCircle, Settings as SettingsIcon } from 'lucide-react';
import { useEffect } from 'react';
import { V4SettingsProvider, useV4 } from './i18n';
import SplashV4 from './Splash';
import InstallPrompt from './InstallPrompt';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/client-pwa-v4',       labelKey: 'nav_today', icon: Home,          end: true },
  { to: '/client-pwa-v4/ask',   labelKey: 'nav_ask',   icon: MessageCircle },
  { to: '/client-pwa-v4/money', labelKey: 'nav_money', icon: Briefcase },
  { to: '/client-pwa-v4/files', labelKey: 'nav_files', icon: FolderOpen },
];

function Shell() {
  const { t, dir, font, settings, themeMode, accent } = useV4();
  const location = useLocation();

  // Override the <html> theme class to match v4's setting while mounted.
  // Tailwind dark variants are triggered by any ancestor .dark, so we MUST
  // set the html class — applying .dark to the v4 root alone isn't enough
  // when the firm panel has already added .dark to <html>. On unmount,
  // restore whatever the firm panel had so it isn't left in the wrong mode.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.classList.contains('dark') ? 'dark' : 'light';
    html.classList.remove('light', 'dark');
    html.classList.add(themeMode);
    return () => {
      html.classList.remove('light', 'dark');
      html.classList.add(prev);
    };
  }, [themeMode]);

  // Inject v4's own PWA manifest + theme-color while this app is mounted.
  // Restores the original ones on unmount so the panel keeps its identity.
  useEffect(() => {
    const head = document.head;
    let manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const prevManifestHref = manifestLink?.href;
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      head.appendChild(manifestLink);
    }
    manifestLink.href = '/client-pwa-v4-manifest.json';

    let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const prevThemeContent = themeMeta?.content;
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      head.appendChild(themeMeta);
    }
    themeMeta.content = accent.primary;

    return () => {
      if (manifestLink && prevManifestHref) manifestLink.href = prevManifestHref;
      if (themeMeta && prevThemeContent) themeMeta.content = prevThemeContent;
    };
  }, [accent.primary]);

  const onSettings = location.pathname.includes('/settings');
  // Home is a minimal launcher — no top header, the firm logo is the header.
  const onHome = location.pathname === '/client-pwa-v4' || location.pathname === '/client-pwa-v4/';
  const hideHeader = onSettings || onHome;

  const sizeClass = settings.fontSize === 'xl' ? 'text-[118%]' : settings.fontSize === 'large' ? 'text-[108%]' : '';

  // CSS variables that drive accent colors throughout v4. Components use
  // bg-[var(--v4-accent)] / text-[var(--v4-accent-text)] etc.
  // The tint flips automatically in dark mode to a low-alpha overlay of the
  // accent so the same class works on both backgrounds.
  const cssVars = {
    fontFamily: font,
    '--v4-accent':       accent.primary,
    '--v4-accent-hover': accent.hover,
    '--v4-accent-text':  themeMode === 'dark' ? accent.primary : accent.text,
    '--v4-accent-tint':  themeMode === 'dark' ? `${accent.primary}24` : accent.tint, // 24 = 14% alpha
    '--v4-accent-soft':  themeMode === 'dark' ? `${accent.primary}33` : `${accent.primary}1A`, // 1A=10%, 33=20%
  } as React.CSSProperties;

  return (
    <div
      className={cn(
        'min-h-screen bg-background text-foreground',
        !hideHeader && 'pb-24',
        sizeClass,
        // v4 owns its own theme — applied to its own root, NOT to <html>.
        // Toggling dark mode here doesn't affect the firm admin panel.
        themeMode === 'dark' && 'dark'
      )}
      style={cssVars}
      dir={dir}
    >
      <SplashV4 />
      <InstallPrompt />

      {!hideHeader && (
        <header className="sticky top-0 z-20 border-b border-border/70 bg-card/95 backdrop-blur-md">
          <div className="mx-auto flex max-w-[720px] items-center justify-between px-4 py-2.5">
            {/* Business identity + accountant chip */}
            <div className="flex items-center gap-3 min-w-0">
              {/* Mini brand mark */}
              <div className="relative h-9 w-9 flex-shrink-0">
                <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 rotate-3" />
                <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 -rotate-3 opacity-90" />
                <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">R</div>
              </div>
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('app_name')}</span>
                <span className="text-[13px] font-semibold truncate">{t('business_name')}</span>
              </div>
            </div>

            <Link
              to="/client-pwa-v4/settings"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
              aria-label="Settings"
            >
              <SettingsIcon className="h-4 w-4" />
            </Link>
          </div>
        </header>
      )}

      <main className="mx-auto max-w-[720px] px-4 pt-4">
        <Outlet />
      </main>

      {/* Bottom tabs */}
      {!hideHeader && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card">
          <div className="mx-auto flex max-w-[720px] items-stretch justify-around">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors',
                    isActive ? 'text-[var(--v4-accent-text)]' : 'text-muted-foreground hover:text-foreground'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute top-0 h-0.5 w-10 rounded-b-full bg-indigo-600" />}
                    <item.icon className={cn('h-5 w-5', isActive && 'stroke-[2.25]')} />
                    <span>{t(item.labelKey)}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

export default function ClientPwaV4Layout() {
  return (
    <V4SettingsProvider>
      <Shell />
    </V4SettingsProvider>
  );
}
